import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, ShieldX, Ban, Award } from 'lucide-react';
import { courseService } from '../../services/course.service';
import type { CertificateVerify } from '@edytech/shared/types';

export const CertificateVerifyPage: React.FC = () => {
  const { serial } = useParams<{ serial: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['certificate-verify', serial],
    queryFn: () => courseService.verifyCertificate(serial!),
    enabled: !!serial,
    retry: false,
  });

  return (
    <div className="container mx-auto max-w-lg px-4 py-16">
      <div className="text-center">
        <Award className="mx-auto h-14 w-14 text-primary" />
        <h1 className="mt-4 text-2xl font-bold">Certificate Verification</h1>
        <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">
          Verifying certificate {serial}
        </p>
      </div>

      <div className="mt-8">
        {isLoading && (
          <div className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-8 text-center text-[rgb(var(--light-secondary))]">
            Verifying certificate...
          </div>
        )}

        {error && !isLoading && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-8 text-center">
            <ShieldX className="mx-auto h-12 w-12 text-red-500" />
            <h2 className="mt-3 text-lg font-semibold text-red-600">Certificate Not Found</h2>
            <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">
              No certificate matches this ID. Check the ID and try again.
            </p>
          </div>
        )}

        {data && !isLoading && <VerifyResult data={data} />}
      </div>
    </div>
  );
};

const VerifyResult: React.FC<{ data: CertificateVerify }> = ({ data }) => {
  // REVOKED is a distinct, authoritative state — never confused with "never existed".
  if (data.revoked) {
    return (
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-8 text-center">
        <Ban className="mx-auto h-12 w-12 text-amber-500" />
        <h2 className="mt-3 text-lg font-semibold text-amber-600">Certificate Revoked</h2>
        <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">
          This certificate has been revoked by the issuing institution and is no longer valid.
        </p>
        {data.revokedAt && (
          <p className="mt-2 text-sm text-[rgb(var(--light-secondary))]">
            Revoked on {new Date(data.revokedAt).toLocaleDateString()}
            {data.revokedReason ? ` — ${data.revokedReason}` : ''}
          </p>
        )}
        <Link to="/courses" className="mt-6 inline-block font-medium text-primary hover:underline">
          Browse courses
        </Link>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-8 text-center ${
      data.valid ? 'border-green-500/40 bg-green-500/10' : 'border-red-500/40 bg-red-500/10'
    }`}>
      {data.valid ? (
        <ShieldCheck className="mx-auto h-12 w-12 text-green-500" />
      ) : (
        <ShieldX className="mx-auto h-12 w-12 text-red-500" />
      )}
      <h2 className={`mt-3 text-lg font-semibold ${data.valid ? 'text-green-600' : 'text-red-600'}`}>
        {data.valid ? 'Genuine Certificate ✓' : 'Invalid Certificate'}
      </h2>
      <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">
        {data.valid
          ? 'This certificate is genuine and issued by EdyTech Academy.'
          : 'This certificate could not be verified. Please check the ID and try again.'}
      </p>

      <dl className="mt-6 space-y-3 text-left">
        <div className="flex justify-between border-b border-[rgb(var(--border))] pb-2">
          <dt className="text-sm text-[rgb(var(--light-secondary))]">Certificate Holder</dt>
          <dd className="text-sm font-semibold">{data.fullName ?? '—'}</dd>
        </div>
        <div className="flex justify-between border-b border-[rgb(var(--border))] pb-2">
          <dt className="text-sm text-[rgb(var(--light-secondary))]">Course</dt>
          <dd className="text-sm font-semibold">{data.courseTitle ?? '—'}</dd>
        </div>
        <div className="flex justify-between border-b border-[rgb(var(--border))] pb-2">
          <dt className="text-sm text-[rgb(var(--light-secondary))]">Certificate ID</dt>
          <dd className="text-sm font-mono">{data.serialNumber ?? '—'}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-sm text-[rgb(var(--light-secondary))]">Issued</dt>
          <dd className="text-sm font-semibold">
            {data.issuedAt ? new Date(data.issuedAt).toLocaleDateString() : '—'}
          </dd>
        </div>
      </dl>

      <Link to="/courses" className="mt-6 inline-block font-medium text-primary hover:underline">
        Browse courses
      </Link>
    </div>
  );
};
